// Shadow Work assessment — the 10 trait spectra + 80-item bank + result-type templates.
//
// Ported verbatim from docs/SHADOW_SPECTRA.md (our own constructs — Jungian-inspired
// but not a clone of any proprietary instrument). Each spectrum has 4 quadrants
// (Left-Positive, Left-Shadow, Right-Positive, Right-Shadow), 2 Likert items each.
//
// ⚠️ Ethical framing (spec §2): self-reflection, NOT diagnosis. De-gendered poles —
// "two complementary inner forces everyone holds". Left pole = the −9 end, Right = +9.

export const SHADOW_ID = 'a1000000-0000-0000-0000-000000000013';

export type Quadrant = 'LP' | 'LS' | 'RP' | 'RS';

export interface ShadowItem {
  code: Quadrant;
  text: string;
}

export interface ShadowSpectrum {
  id: string;
  order: number;
  name: string;
  /** The −9-end pole. */
  leftPole: string;
  /** The +9-end pole. */
  rightPole: string;
  /** Short expression labels per quadrant (healthy/shadow of each pole). */
  expressions: Record<Quadrant, string>;
  integrationPractice: string;
  /** Accent color for cards / direction bar. */
  color: string;
  /** 8 items, in LP,LP,LS,LS,RP,RP,RS,RS order. */
  items: ShadowItem[];
}

export type ShadowResultType =
  | 'integrated_flexible'
  | 'healthy_left_leaning'
  | 'healthy_right_leaning'
  | 'left_shadow_dominant'
  | 'right_shadow_dominant'
  | 'repressed_counterpart'
  | 'inner_conflict'
  | 'low_access';

export interface ResultTemplate {
  /** Short chip label. */
  label: string;
  /** Developmental read (never a fixed label). */
  read: string;
  /** Growth direction. */
  growth: string;
}

// §5 templates. Placeholders: {L}=leftPole, {R}=rightPole, {dominant}/{opposite}
// resolved per result in buildSpectrumResult.
export const SHADOW_RESULT_TEMPLATES: Record<ShadowResultType, ResultTemplate> = {
  integrated_flexible: {
    label: 'Integrated & flexible',
    read: 'You move fluidly between {L} and {R}, in healthy form.',
    growth: 'Maintain flexibility; choose the trait intentionally by context.',
  },
  healthy_left_leaning: {
    label: 'Healthy {L}-leaning',
    read: 'You lean toward {L}, expressed in a healthy way.',
    growth: 'Keep the strength; practice {R} lightly to widen your range.',
  },
  healthy_right_leaning: {
    label: 'Healthy {R}-leaning',
    read: 'You lean toward {R}, expressed in a healthy way.',
    growth: 'Keep the strength; practice {L} lightly to widen your range.',
  },
  left_shadow_dominant: {
    label: '{L} in shadow',
    read: '{L} may be over-relied on and slipping into its shadow under stress.',
    growth: 'Ease the overuse; practice the healthy form of {R}.',
  },
  right_shadow_dominant: {
    label: '{R} in shadow',
    read: '{R} may be over-relied on and slipping into its shadow under stress.',
    growth: 'Ease the overuse; practice the healthy form of {L}.',
  },
  repressed_counterpart: {
    label: '{opposite} disowned',
    read: '{dominant} is healthy and dominant, but {opposite} appears disowned — possibly avoided after seeing its shadow form.',
    growth: 'Reframe the rejected trait apart from its extreme; practice its healthy form in safe settings.',
  },
  inner_conflict: {
    label: 'Inner conflict',
    read: 'Both the healthy and shadow sides score high — the trait is emotionally charged for you right now.',
    growth: 'Notice the triggers and state-shifts that flip you between them.',
  },
  low_access: {
    label: 'Low access',
    read: 'Neither side is strongly active or clear here.',
    growth: 'Build basic awareness and language for both poles before pushing either.',
  },
};

/** Substitute pole placeholders in a template string. */
export function fillTemplate(
  text: string,
  poles: { left: string; right: string; dominant?: string; opposite?: string },
): string {
  return text
    .replace(/\{L\}/g, poles.left)
    .replace(/\{R\}/g, poles.right)
    .replace(/\{dominant\}/g, poles.dominant ?? '')
    .replace(/\{opposite\}/g, poles.opposite ?? '');
}

export const SHADOW_SPECTRA: ShadowSpectrum[] = [
  {
    id: 'emotional_expression',
    order: 1,
    name: 'Emotional Expression',
    leftPole: 'Expressiveness',
    rightPole: 'Restraint',
    expressions: {
      LP: 'Open, honest, vulnerable expression',
      LS: 'Overwhelming or manipulative intensity',
      RP: 'Composure and good timing',
      RS: 'Suppression, numbness, emotional unavailability',
    },
    integrationPractice:
      'Before reacting, name the feeling and choose whether to express it or hold it — both are valid moves.',
    color: '#ef4444',
    items: [
      { code: 'LP', text: 'I can openly express what I feel without feeling ashamed.' },
      { code: 'LP', text: 'I allow myself to be emotionally vulnerable with trusted people.' },
      { code: 'LS', text: 'My emotions sometimes overwhelm the situation.' },
      { code: 'LS', text: 'I sometimes use emotional intensity to influence others.' },
      { code: 'RP', text: 'I can stay emotionally composed under pressure.' },
      { code: 'RP', text: 'I know when it is better to pause before expressing emotion.' },
      { code: 'RS', text: 'I often suppress emotions until I feel numb or disconnected.' },
      { code: 'RS', text: 'People sometimes experience me as emotionally unavailable.' },
    ],
  },
  {
    id: 'cognitive_processing',
    order: 2,
    name: 'Cognitive Processing',
    leftPole: 'Intuition',
    rightPole: 'Logic',
    expressions: {
      LP: 'Pattern-sensing, trusting examined insight',
      LS: 'Ungrounded assumptions, attachment to signs',
      RP: 'Careful analysis and reasoning',
      RS: 'Over-analysis/paralysis, dismissing the unproven',
    },
    integrationPractice: 'Pair each hunch with one fact-check, and each analysis with one gut-check.',
    color: '#f59e0b',
    items: [
      { code: 'LP', text: 'I often sense patterns before I can fully explain them.' },
      { code: 'LP', text: 'I trust my intuition when making complex decisions.' },
      { code: 'LS', text: 'I sometimes follow a feeling without checking the facts.' },
      { code: 'LS', text: 'I can become attached to signs, feelings, or assumptions without evidence.' },
      { code: 'RP', text: 'I analyze problems carefully before deciding.' },
      { code: 'RP', text: 'I prefer conclusions that are supported by clear reasoning.' },
      { code: 'RS', text: 'I overanalyze situations until I delay action.' },
      { code: 'RS', text: 'I dismiss ideas too quickly if they do not seem logical at first.' },
    ],
  },
  {
    id: 'interpersonal_approach',
    order: 3,
    name: 'Interpersonal Approach',
    leftPole: 'Receptivity',
    rightPole: 'Assertiveness',
    expressions: {
      LP: 'Listening, openness, adaptability',
      LS: 'Passivity, self-silencing, indecisiveness',
      RP: 'Clear needs, healthy boundaries',
      RS: 'Pushiness, dismissiveness, domination',
    },
    integrationPractice:
      'In conflict, ask "What is the other person seeing that I may be missing?" before stating your position.',
    color: '#10b981',
    items: [
      { code: 'LP', text: 'I listen carefully before forming a response.' },
      { code: 'LP', text: 'I can genuinely consider perspectives different from mine.' },
      { code: 'LS', text: 'I avoid expressing my opinion to prevent discomfort.' },
      { code: 'LS', text: 'I often let others decide even when I have a clear preference.' },
      { code: 'RP', text: 'I can express my needs directly and respectfully.' },
      { code: 'RP', text: 'I am comfortable setting boundaries when needed.' },
      { code: 'RS', text: 'I sometimes push my view too strongly.' },
      { code: 'RS', text: 'I can become dismissive when others disagree with me.' },
    ],
  },
  {
    id: 'caregiving_style',
    order: 4,
    name: 'Caregiving Style',
    leftPole: 'Nurturing',
    rightPole: 'Protecting',
    expressions: {
      LP: 'Care that helps others grow',
      LS: 'Over-giving, self-neglect, enabling',
      RP: 'Healthy limits, responsibility',
      RS: 'Controlling, infantilizing others',
    },
    integrationPractice: 'Before helping, ask "Does this help them grow, or keep them dependent on me?"',
    color: '#ec4899',
    items: [
      { code: 'LP', text: 'I support people in a way that helps them grow.' },
      { code: 'LP', text: 'I can offer care without needing control.' },
      { code: 'LS', text: 'I sometimes over-give until I neglect myself.' },
      { code: 'LS', text: 'I may enable people by protecting them from consequences.' },
      { code: 'RP', text: 'I can protect people by setting clear limits.' },
      { code: 'RP', text: "I take responsibility when someone's safety or wellbeing is at risk." },
      { code: 'RS', text: 'I can become controlling when I believe I am protecting someone.' },
      { code: 'RS', text: 'I sometimes treat others as if they cannot handle things without me.' },
    ],
  },
  {
    id: 'action_orientation',
    order: 5,
    name: 'Action Orientation',
    leftPole: 'Allowing',
    rightPole: 'Initiative',
    expressions: {
      LP: 'Patience, acceptance, right timing',
      LS: 'Inertia, helplessness, "life happens to me"',
      RP: 'Action, momentum, follow-through',
      RS: 'Restlessness, forcing, impatience',
    },
    integrationPractice:
      'For each thing you\'re waiting on, ask "Is this wise patience or avoidance?" — and act on the second.',
    color: '#6366f1',
    items: [
      { code: 'LP', text: 'I can wait for the right timing instead of forcing action.' },
      { code: 'LP', text: 'I can accept what cannot be controlled.' },
      { code: 'LS', text: 'I delay action even when action is clearly needed.' },
      { code: 'LS', text: 'I sometimes feel like life happens to me and I cannot change it.' },
      { code: 'RP', text: 'I take initiative when something needs to move forward.' },
      { code: 'RP', text: 'I can turn intention into action quickly.' },
      { code: 'RS', text: 'I become restless when things do not move at my pace.' },
      { code: 'RS', text: 'I sometimes push action before people or conditions are ready.' },
    ],
  },
  {
    id: 'focus_orientation',
    order: 6,
    name: 'Focus Orientation',
    leftPole: 'Relationship-Oriented',
    rightPole: 'Task-Oriented',
    expressions: {
      LP: 'Empathy, connection, harmony',
      LS: 'Conflict-avoidance, approval-seeking',
      RP: 'Focus, organization, outcomes',
      RS: 'Results over people, treating emotion as an obstacle',
    },
    integrationPractice:
      'Before a decision, check both "Will this reach the goal?" and "How will this land for people?"',
    color: '#0ea5e9',
    items: [
      { code: 'LP', text: 'I pay attention to how decisions affect people emotionally.' },
      { code: 'LP', text: 'I naturally build connection and harmony in groups.' },
      { code: 'LS', text: 'I avoid necessary conflict to keep the relationship comfortable.' },
      { code: 'LS', text: 'I sometimes seek approval at the expense of what is right.' },
      { code: 'RP', text: 'I can stay focused on goals and outcomes.' },
      { code: 'RP', text: 'I organize effort around clear tasks and priorities.' },
      { code: 'RS', text: "I can become too focused on results and forget people's needs." },
      { code: 'RS', text: 'I may treat emotions as obstacles to efficiency.' },
    ],
  },
  {
    id: 'perception_perspective',
    order: 7,
    name: 'Perception Perspective',
    leftPole: 'Subjectivity',
    rightPole: 'Objectivity',
    expressions: {
      LP: 'Honoring lived/emotional truth',
      LS: 'Feelings-as-facts, over-personalizing',
      RP: 'Fair stepping-back, separating fact from story',
      RS: 'Cold detachment, dismissing experience',
    },
    integrationPractice:
      'Hold your view as "this is how it looks to me," then ask how it looks to someone else.',
    color: '#8b5cf6',
    items: [
      { code: 'LP', text: 'I understand how personal experience shapes perception.' },
      { code: 'LP', text: 'I can honor emotional truth without needing it to be universal.' },
      { code: 'LS', text: 'I sometimes treat my feelings as facts.' },
      { code: 'LS', text: 'I take things personally even when they may not be about me.' },
      { code: 'RP', text: 'I can step back and evaluate a situation fairly.' },
      { code: 'RP', text: 'I try to separate facts from interpretations.' },
      { code: 'RS', text: 'I can become overly detached when people need understanding.' },
      { code: 'RS', text: 'I dismiss subjective experience if it cannot be objectively proven.' },
    ],
  },
  {
    id: 'thinking_style',
    order: 8,
    name: 'Thinking Style',
    leftPole: 'Creativity',
    rightPole: 'Structure',
    expressions: {
      LP: 'Idea generation, imagination',
      LS: 'Unfinished starts, impracticality',
      RP: 'Systems, plans, follow-through',
      RS: 'Rigidity, over-control that limits creativity',
    },
    integrationPractice: 'Give each new idea one concrete next step; give each plan room for one improvisation.',
    color: '#14b8a6',
    items: [
      { code: 'LP', text: 'I generate new ideas easily.' },
      { code: 'LP', text: 'I can imagine possibilities beyond the obvious path.' },
      { code: 'LS', text: 'I start many ideas but struggle to complete them.' },
      { code: 'LS', text: 'My imagination can become disconnected from practical reality.' },
      { code: 'RP', text: 'I create systems that help ideas become real.' },
      { code: 'RP', text: 'I work well with plans, structure, and clear steps.' },
      { code: 'RS', text: 'I can become rigid when plans change.' },
      { code: 'RS', text: 'I may over-control details and limit creativity.' },
    ],
  },
  {
    id: 'ethical_reasoning',
    order: 9,
    name: 'Ethical Reasoning',
    leftPole: 'Empathy',
    rightPole: 'Justice',
    expressions: {
      LP: 'Understanding, compassion',
      LS: "Excusing harm, absorbing others' suffering",
      RP: 'Fairness, accountability without hatred',
      RS: 'Harshness, prioritizing punishment over repair',
    },
    integrationPractice: 'Pair accountability with understanding — name the harm and the humanity together.',
    color: '#f43f5e',
    items: [
      { code: 'LP', text: 'I try to understand why someone acted the way they did.' },
      { code: 'LP', text: 'I can respond to mistakes with compassion.' },
      { code: 'LS', text: 'I sometimes excuse harmful behavior because I understand the pain behind it.' },
      { code: 'LS', text: "I absorb other people's suffering until it affects my judgment." },
      { code: 'RP', text: 'I believe actions should have fair consequences.' },
      { code: 'RP', text: 'I can hold people accountable without hating them.' },
      { code: 'RS', text: 'I can become harsh when someone violates my values.' },
      { code: 'RS', text: 'I sometimes care more about punishment than repair.' },
    ],
  },
  {
    id: 'source_of_knowledge',
    order: 10,
    name: 'Source of Knowledge',
    leftPole: 'Inner Wisdom',
    rightPole: 'External Knowledge',
    expressions: {
      LP: 'Reflection, examined personal insight',
      LS: 'Rumination, closed to outside input',
      RP: 'Informed, learns from others',
      RS: 'Info-overload, over-reliance on authority',
    },
    integrationPractice:
      'For each big question, consult one trusted external source and your own examined intuition, then compare.',
    color: '#a855f7',
    items: [
      { code: 'LP', text: 'I can reflect deeply and learn from my inner experience.' },
      { code: 'LP', text: 'I trust personal insight when it has been carefully examined.' },
      { code: 'LS', text: 'I can become trapped in overthinking my inner world.' },
      { code: 'LS', text: 'I sometimes reject outside input because it does not match my inner feeling.' },
      { code: 'RP', text: 'I seek reliable information before forming conclusions.' },
      { code: 'RP', text: 'I learn from experts, research, and external feedback.' },
      { code: 'RS', text: 'I consume too much information without integrating it.' },
      { code: 'RS', text: 'I rely on external authorities more than my own judgment.' },
    ],
  },
];

export const shadowSpectrumById: Record<string, ShadowSpectrum> = Object.fromEntries(
  SHADOW_SPECTRA.map(s => [s.id, s]),
);

export const SHADOW_SPECTRUM_IDS: string[] = SHADOW_SPECTRA.map(s => s.id);

/** The non-diagnostic disclaimer, shown on the intro + results (spec §2). */
export const SHADOW_DISCLAIMER =
  'This assessment is for self-reflection and growth. It is not a clinical diagnosis or a substitute for professional support.';

/** The guiding principle (results headline, spec §3). */
export const SHADOW_PRINCIPLE =
  "Wholeness isn't living in the middle — it's having conscious access to both sides, and using the right one, in the right way, at the right time.";
